// Angular
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  AccountLookupService,
  AlgoHedgeConfigService,
  BmarkFixingData,
  CcyPairLookupService,
  ClientLookupService,
  CubiqWorker,
  HedgeOrderMonitorStagingTableDefinitionFactory,
  OrderDataActionPromiseService,
  ProfilePromiseActionService,
  TraderGroupLookupService,
  RateCache,
  Rate,
} from "shared/cubiq-services";
import {
  COLUMN_DEALT_DIR_DATA,
  COLUMN_ORDER_AMOUNT_DATA,
  COLUMN_ORDER_TYPE,
} from "shared/cubiq-services/model/BmarkClientOrdersTableDefinitionFactory.constants";
import {
  COLUMN_ACCOUNT_ID,
  COLUMN_CCY_PAIR_CODE,
  COLUMN_END_TIME,
  COLUMN_PRIVATE_COMMENT,
  COLUMN_START_TIME,
} from "shared/cubiq-services/model/HedgeOrderMonitorStagingTableDefinitionFactory.constants";
import { COLUMN_TRADER_GROUP_NAMES } from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { BfxColDef, TableDefinition } from "shared/cubiq-services/model/TableDefinition";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
// Ag Grid + Other
import {
  CellClickedEvent,
  CellEditingStartedEvent,
  Column,
  GridOptions,
  RowNode,
  RowSelectedEvent,
  TabToNextCellParams,
} from "ag-grid-community";
import { ColumnState } from "ag-grid-community/dist/lib/columnController/columnController";
import { Subject, Subscription } from "rxjs";
import { BfxBaseBlotterColumnSelectorService } from "../bfx-base-blotter/bfx-base-blotter-columnselector.service";
import { BfxAuthService } from "../bfx-common/services/bfx-auth.service";
import { DfAmountSpinner } from "../bfx-detail-fill/editors/DfAmountSpinner";
import { DfAutoComplete } from "../bfx-detail-fill/editors/DfAutoComplete";
import { BfxDialogCommand } from "../bfx-dialogs/models/bfx-dialog-command.enum";
import { BfxDialogData } from "../bfx-dialogs/models/bfx-dialog-data.model";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { GoldenLayoutService } from "../golden-layout/golden-layout-service";
import { ICAutocompleteGenericModel } from "../order-entry/interface/controls/models/bfx-interface-controls.models";
import { BfxHedgeOrderMonitorComponent } from "./bfx-hedge-order-monitor.component";
import { BfxHedgeOrderMonitorService } from "./bfx-hedge-order-monitor.service";
import {
  BfxHedgeOrderStrategyParametersComponent,
  BfxHedgeOrderStrategyParamsConfig,
} from "./bfx-hedge-order-strategy-parameters.component";
import { CommentEditor } from "./editors/CommentEditor";
import { IntentEditor } from "./editors/IntentEditor";
import { TimeEditor } from "./editors/TimeEditor";
import { LockedStagingRowRenderer } from "./renderers/LockedStagingRowRenderer";
import { StrategyParametersRenderer } from "./renderers/StrategyParametersRenderer";
import { BfxHedgeOrderMonitorUtilService } from "./services/bfx-hedge-order-monitor-util.service";
import { BfxHedgeOrderStagingRow } from "../../shared/cubiq-services/model/benchmarks/BfxHedgeOrderStagingRow";
import { v4 as uuid } from "uuid";
import { BFXDomHelper } from "app/bfx-common/helpers/bfx-dom-helper";
import { DomHandler } from "@bfx/primeng/dom";
import { AgGridEditableBodyScroll } from "../shared/ag-grid/AgGridEditableBodyScroll";
import { NativeDesktopWindow } from "shared/native-desktop/NativeDesktopWindow";
import { NullProtoObject } from "../../shared/helper-services/null-proto-object";
// import { ColumnState } from 'ag-grid-community/dist/lib/columnController/columnController';

// Constants
const DEFAULT_ROW_HEIGHT: number = 29;
const MANUAL_EXTERNAL_ORDER_TYPES: any[] = [{ label: "BMHO", value: "BMHO" }];
const TIME_COLUMNS: string[] = ["startTime", "endTime"];

export const ERROR_ARGS_TIME_FORMAT: string = "ddd MMM Do YYYY HH:mm:ss Z";

@Component({
  selector: "bfx-hedge-order-staging-grid",
  templateUrl: "./html/bfx-hedge-order-staging-grid.component.html",
  providers: [BfxHedgeOrderMonitorService],
  standalone: false,
})
export class BfxHedgeOrderStagingGridComponent implements OnInit {
  protected authSubscription: Subscription;
  protected saveProfileSubscription: Subscription;

  // Grid definition
  public blotterId: string;
  public blotterInstance: any = null;
  public blotterView: ElementRef;
  public columnDefs: Array<BfxColDef> = [];
  public restoredColumnDefs: Array<BfxColDef> = [];
  public gridOptions: GridOptions;
  public rowHeight = DEFAULT_ROW_HEIGHT;
  public tableDefinition: TableDefinition = null;
  public primed: boolean = false;
  public isIntentStyled: boolean = true;
  public hasProfileConfig: boolean = false;
  public data: NullProtoObject = Object.create(null);
  public bulkRowId: string = null;

  private traderGroupLookupValues: Array<any> = [];
  private accountLookupValues: Array<any> = [];
  private currencyPairLookupValues: Array<any> = [];
  private orderTypeLookupValues: any = {};

  private fixingData: BmarkFixingData = null;
  private defaultAccountMappings: CubiqTS.DefaultAccountProfileMappingImpl[] = [];

  private _panelClosing: boolean = false;

  private orderLifecycleEventSubscription: Subscription = null;

  public selectionChangedSubject: Subject<RowNode[]> = new Subject<RowNode[]>();

  @ViewChild("strategyParameters", { static: true })
  private strategyParametersDialog: BfxHedgeOrderStrategyParametersComponent;

  @Input("columnsConfig")
  public columnsConfiguration: any = {};

  @Input()
  get panelClosing(): boolean {
    return this._panelClosing;
  }

  set panelClosing(closed: boolean) {
    this._panelClosing = closed;
    if (this._panelClosing)
      // this.bfxHedgeOrderMonitorService.setStagingBlotterColumnsConfig(this.getBlotterColumnsConfigs())
      this.bfxHedgeOrderMonitorService.saveConfigToSession(
        this.blotterId,
        this.getBlotterColumnsConfigs(),
      );
  }

  @Output("selectedOrdersChange")
  public selectedOrdersEmitter: EventEmitter<any> = new EventEmitter();

  @Output("gridLayoutChange")
  public gridLayoutChangeEmitter: EventEmitter<any> = new EventEmitter();

  @Output("bulkEditChange")
  public bulkEditEmitter: EventEmitter<any> = new EventEmitter();

  @Output("gridReady")
  public gridReady: EventEmitter<any> = new EventEmitter();

  private stagingOrderCount: number = 0;

  public frameworkComponents: any = {
    amountEditor: DfAmountSpinner,
    autoCompleteEditor: DfAutoComplete,
    intentEditor: IntentEditor,
    timeEditor: TimeEditor,
    commentEditor: CommentEditor,
    intentRenderer: IntentEditor,
    lockedRenderer: LockedStagingRowRenderer,
    strategyParamRenderer: StrategyParametersRenderer,
  };

  constructor(
    private bfxHedgeOrderMonitorService: BfxHedgeOrderMonitorService,
    private hedgeOrdersStagingTableDefinition: HedgeOrderMonitorStagingTableDefinitionFactory,
    private element: ElementRef,
    protected authService: BfxAuthService,
    protected blotterElement: ElementRef,
    protected columnSelectorService: BfxBaseBlotterColumnSelectorService,
    protected layoutService: GoldenLayoutService,
    protected profileActionService: ProfilePromiseActionService,
    protected traderGroupLookupService: TraderGroupLookupService,
    protected accountLookupService: AccountLookupService,
    protected clientLookupService: ClientLookupService,
    protected currencyPairLookupService: CcyPairLookupService,
    protected algoHedgeService: AlgoHedgeConfigService,
    protected orderDataActionService: OrderDataActionPromiseService,
    protected translateService: TranslateService,
    protected rateCache: RateCache,
    protected hedgeOrderMonitorUtilService: BfxHedgeOrderMonitorUtilService,
    protected cubiqWorker: CubiqWorker,
    public zone: NgZone,
  ) {
    (this.constructor as any).instanceId++;
    this.blotterId = this.constructor.name; // + "_" + (this.constructor as any).instanceId;
    this.blotterInstance = this;
    this.blotterView = blotterElement;
    this.tableDefinition = hedgeOrdersStagingTableDefinition.createDef();

    this.subscribeToAuth();
    this.subscribeToSaveProfile();
    this.initializeGrid();
    this.initializeLookupValues();

    this.orderLifecycleEventSubscription = this.cubiqWorker
      .getEncodableObservable(CubiqTS.OrderLifeCycleEventImpl.name)
      .subscribe(this.processOrderLifecycleEvent.bind(this));
  }

  protected initializeLookupValues() {
    this.traderGroupLookupValues = this.traderGroupLookupService
      .getAll()
      .filter((tg: CubiqTS.TradingGroupImpl) => tg.isBenchmarkFg && tg.isAutoFilledFg)
      .map((tg: CubiqTS.TradingGroupImpl) => {
        return { label: tg.groupCode, value: tg.groupId.toString() };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    this.accountLookupValues = this.accountLookupService
      .getAll()
      .filter(
        (account: CubiqTS.AccountImpl) =>
          account.bookingType == CubiqTS.AccountBookingType.RISK_ROUTING,
      )
      .map((a: CubiqTS.AccountImpl) => {
        return { label: a.name, value: a.id.toString() };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    this.currencyPairLookupValues = this.currencyPairLookupService
      .getAll()
      .filter((pair: CubiqTS.CurrencyPairImpl) => pair.allowBmarkSpotOrdersFg)
      .map((c: CubiqTS.CurrencyPairImpl) => {
        return { label: c.base + c.nonBase, value: c.base + c.nonBase };
      })
      .sort((a: any, b: any) => a.label.localeCompare(b.label));

    this.algoHedgeService
      .getAll()
      .filter((config: CubiqTS.AlgoHedgeConfigInfoImpl) => config.enabledFg)
      .forEach((value: CubiqTS.AlgoHedgeConfigInfoImpl) => {
        let configs: Array<any> = this.orderTypeLookupValues[value.traderGroupNm];
        if (configs == null) {
          this.orderTypeLookupValues[value.traderGroupNm] = configs = [];
        }
        configs.push({
          label: value.algoProfile,
          value: value.algoHedgeId.toString(),
        });
      });
  }

  public initializeGrid(): void {
    let funcGetRowNodId = function (data) {
      if (data && this.tableDefinition != null) {
        return String(data[this.tableDefinition.primaryKey]);
      }
      return "";
    }.bind(this);

    const doc = DomHandler.getDocument(this.element);
    const cellHeight = BFXDomHelper.getVariablePx(doc, "--ds-cont-sizing-common-sm", 30);
    const border = BFXDomHelper.getVariablePx(doc, "--ds-cont-border-width-common-subtle", 30);
    const rowHeight = cellHeight + border;

    this.gridOptions = <GridOptions>{
      alignedGrids: [],
      deltaRowDataMode: true,
      enableBrowserTooltips: true,
      enableFilter: false,
      getRowNodeId: funcGetRowNodId,
      headerHeight: rowHeight,
      rowDeselection: false,
      rowSelection: "multiple",
      showToolPanel: false,
      sortable: false,
      stopEditingWhenGridLosesFocus: true,
      suppressBrowserResizeObserver: NativeDesktopSupport.isNativeDesktopContainer(),
      suppressAutoSize: true,
      suppressColumnMoveAnimation: true,
      suppressContextMenu: true,
      suppressDragLeaveHidesColumns: true,
      suppressFocusAfterRefresh: true,
      suppressMovable: true,
      suppressRowClickSelection: true,
      suppressScrollOnNewData: true,
      rowHeight: rowHeight,
      suppressTabbing: false,
      toolPanelSuppressSideButtons: true,
      onBodyScroll: AgGridEditableBodyScroll(this),
      onCellClicked: (event: CellClickedEvent) => {
        if (event.colDef.editable) {
          if (event.column) {
            this.gridOptions.api.ensureColumnVisible(event.column);
          }
          event.api.startEditingCell({
            rowIndex: event.rowIndex,
            colKey: event.colDef.field,
          });
        }
      },
      onCellEditingStarted: (event: CellEditingStartedEvent) => {
        if (event.colDef.field == COLUMN_PRIVATE_COMMENT) {
          try {
            event.api.getCellEditorInstances()[0]["eInput"].maxLength = 250;
          } catch (e) {}
        }
      },
      tabToNextCell: ((params: TabToNextCellParams) => {
        if (
          params != null &&
          params.nextCellPosition != null &&
          params.nextCellPosition.rowIndex == 0 &&
          this.hasBulkEditRow() &&
          params.nextCellPosition.column != null &&
          TIME_COLUMNS.includes(params.nextCellPosition.column.getColId())
        ) {
          let column: Column = this.gridOptions.columnApi.getDisplayedColAfter(
            params.nextCellPosition.column,
          );
          if (column != null && TIME_COLUMNS.includes(column.getColId())) {
            column = this.gridOptions.columnApi.getDisplayedColAfter(column);
          }

          let rowIndex: number = params.nextCellPosition.rowIndex;
          if (column == null) {
            let values: Array<BfxHedgeOrderStagingRow> = Object.values(this.data);
            if (values.length > rowIndex + 1) {
              values.sort((item) => (item.bulkEditRow ? -1 : 0));

              let allDisplayedColumns: Column[] =
                this.gridOptions.columnApi.getAllDisplayedColumns();
              let data: any = values[rowIndex++];
              column = allDisplayedColumns.find((column: Column) =>
                this.isCellEditingEnabled({
                  node: { data: data },
                  colDef: column.getColDef(),
                }),
              );
            }
          }

          if (column == null) {
            return params.nextCellPosition;
          }

          return {
            rowPinned: null,
            rowIndex: rowIndex,
            column: column,
          };
        }

        return params.nextCellPosition;
      }).bind(this),
      treeData: true,
      context: {
        toggleLocked: this.toggleLocked.bind(this),
        getAutoCompleteValues: this.getAutoCompleteValues.bind(this),
        getAutoCompleteSelectedValue: this.getAutoCompleteSelectedValue.bind(this),
        isCellEditingEnabled: this.isCellEditingEnabled.bind(this),
        shouldRoundToMillions: this.shouldRoundToMillions.bind(this),
        setEditedValue: this.setEditedValue.bind(this),
        editStrategyParameters: this.editStrategyParameters.bind(this),
      },

      onGridReady: (params) => {
        // rest of initialization happens after ag-grid is ready
        if (this.gridOptions != null) {
          this.gridOptions.api = params.api;
          this.gridOptions.columnApi = params.columnApi;

          if (this.gridOptions.api != null) {
            this.gridOptions.api.showLoadingOverlay();
          }
        }

        this.hedgeOrdersStagingTableDefinition.defineTable().then(
          (value) => {
            this.tableDefinition = value;
            this.initializeColumnDefinitions();
            this.gridOptions.api.setRowData([]);
            this.gridOptions.api.sizeColumnsToFit();
            this.gridOptions.api.hideOverlay();
            // this.gridOptions.api.refreshCells();
          },
          (reason: string) => {
            logger.out.info(
              "BfxHedgeOrderStagingGridComponent: hedgeOrdersStagingTableDefinition.defineTable rejection: " +
                reason,
            );
          },
        );
      },

      // tabToNextCell: (params: TabToNextCellParams) => {
      // 	if (
      // 		params.nextCellPosition && params.nextCellPosition.column &&
      // 		params.nextCellPosition.column.getColDef().editable
      // 	) {
      // 		let col = this.gridColumnApi.getDisplayedColAfter(params.nextCellPosition.column);
      // 		if (col) {
      // 			this.gridOptions.api.ensureColumnVisible(col);
      // 		} else {
      // 			this.gridOptions.api.ensureColumnVisible(params.nextCellPosition.column);
      // 		}
      // 	}
      // 	if (params.nextCellPosition) {
      // 		return {
      // 			rowIndex: params.nextCellPosition.rowIndex,
      // 			column: params.nextCellPosition.column,
      // 			rowPinned: params.nextCellPosition.rowPinned
      // 		};
      // 	}
      // 	return null;
      // },
      // Not sure if this should be available
      onColumnMoved: () => {
        // need to update the grid layout
        this.gridLayoutChangeEmitter.emit();
      },
      onColumnVisible: () => {
        // need to update the grid layout
        this.gridLayoutChangeEmitter.emit();
      },
      getDataPath: (data) => {
        return data.key.split("-");
      },
      groupDefaultExpanded: -1,
      groupSuppressAutoColumn: true,
      isRowSelectable: (row) => {
        if (row != null && row.data != null && row.data.bulkEditRow) {
          return false;
        }
        if (!row.data || row.data.isComponentLeg) {
          return false;
        }
        return true;
      },
      onRowSelected: (event: RowSelectedEvent) => {
        if (event.type === "rowSelected") {
          if (event.node.isSelected()) {
            // if (event.data.leg1 && event.data.leg2) {
            // 	event.data.leg1.legEnabled = true;
            // 	event.data.leg2.legEnabled = true;
            // 	this.gridOptions.api.batchUpdateRowData({ update: [event.data.leg1, event.data.leg2] });
            // }
            logger.out.info(
              "BfxHedgeOrderStagingGridComponent -> onRowSelected:",
              event.node.isSelected(),
            );
          } else {
            // if (event.data.leg1 && event.data.leg2) {
            // 	event.data.leg1.legEnabled = false;
            // 	event.data.leg2.legEnabled = false;
            // 	this.gridOptions.api.batchUpdateRowData({ update: [event.data.leg1, event.data.leg2] });
            // }
            logger.out.info(
              "BfxHedgeOrderStagingGridComponent -> onRowSelected:",
              event.node.isSelected(),
            );
          }
        }
      },
    };

    NativeDesktopSupport.prepareGridOptions(this.gridOptions, this.blotterElement.nativeElement);
  }

  public initDefaultAccountMappings(
    fixingData: BmarkFixingData,
    values: CubiqTS.DefaultAccountProfileMappingImpl[],
  ): void {
    this.fixingData = fixingData;
    this.defaultAccountMappings = values;
    this.gridReady.emit();
  }

  protected toggleLocked(id: string): void {
    let row: BfxHedgeOrderStagingRow = this.data[id];

    if (row != null && !row.processing) {
      row.locked = !row.locked;
      setTimeout(
        (() => {
          if (this.gridOptions && this.gridOptions.api) this.gridOptions.api.refreshCells();
        }).bind(this),
        10,
      );
    }
  }

  // Lifecycle Events:
  ngOnInit() {
    this.restoredColumnDefinitions();
  }

  ngOnDestroy() {
    if (this.orderLifecycleEventSubscription != null) {
      this.orderLifecycleEventSubscription.unsubscribe();
    }

    if (this.saveProfileSubscription != null) {
      this.saveProfileSubscription.unsubscribe();
    }
  }

  protected initializeColumnDefinitions(): void {
    this.gridOptions.onSelectionChanged = () => {
      if (this.gridOptions != null && this.gridOptions.columnApi != null) {
        let col = this.gridOptions.columnApi.getColumn("selection");
        let colDef = col.getColDef();
        colDef.headerTooltip = colDef.headerName;
        this.gridOptions.api.refreshHeader();
        this.selectionChangedSubject.next(this.gridOptions.api.getSelectedNodes());
      }
    };

    this.applyColumnDefinitions();
  }

  protected applyColumnDefinitions(): void {
    let defs: Array<BfxColDef> = this.tableDefinition.columns.concat();
    defs.forEach((def) => {
      this.applyColumnDefinition(def);
    });
    this.columnDefs = defs;
    if (this.gridOptions != null && this.gridOptions.columnApi != null) {
      this.gridOptions.columnApi.setColumnState(defs as ColumnState[]);
    }
  }

  protected restoredColumnDefinitions() {
    let columnsConfigs: any = this.bfxHedgeOrderMonitorService.restoreConfigFromSession(
      this.blotterId,
    );

    if (
      columnsConfigs &&
      typeof columnsConfigs === "object" &&
      Object.keys(columnsConfigs).length !== 0
    ) {
      if (columnsConfigs.hasBulkEditRow === true) {
        this.bulkEditEmitter.emit(columnsConfigs.hasBulkEditRow);
      }

      delete columnsConfigs.hasBulkEditRow;

      this.restoredColumnDefs = Object.values(columnsConfigs);
    } else {
      this.bfxHedgeOrderMonitorService.getProfileConfig().then((config: any) => {
        if (config && Object.keys(config.stagingBlotterConfiguration).length !== 0) {
          try {
            columnsConfigs = JSON.parse(config.stagingBlotterConfiguration);
            if (columnsConfigs.hasBulkEditRow === true) {
              this.bulkEditEmitter.emit(columnsConfigs.hasBulkEditRow);
            }

            delete columnsConfigs.hasBulkEditRow;
            this.restoredColumnDefs = Object.values(columnsConfigs);
          } catch (e) {
            logger.out.warn(
              this.constructor.name + ": Failed to restore column configuration " + e,
            );
          }
        }
      });
    }
  }

  protected applyColumnDefinition(column: BfxColDef): any {
    let col: BfxColDef = Object.assign({}, column);

    if (this.restoredColumnDefs) {
      this.restoredColumnDefs.forEach((def) => {
        if (def.field == column.field) {
          column.hide = def.hide;
          column.disableInPicker = def.disableInPicker;
        }
      });
    }

    column.headerClass = "ag-header-cell-bordered";
    column.suppressMovable = true;

    return col;
  }

  protected checkForNoRows(): void {
    if (this.primed && this.gridOptions.api.getModel()["rootNode"].childrenAfterFilter.length == 0)
      this.gridOptions.api.showNoRowsOverlay();
    else if (this.primed) this.gridOptions.api.hideOverlay();
  }

  // override to perform any actions after data for the blotter is primed
  protected onAdaptedDataPrimed(): void {
    this.primed = true;
  }

  protected selectedRowsLength(): number {
    return this.gridOptions.api.getSelectedRows().length;
  }

  /**
   * @description Gets triggered when a user logs out, and overwrites profile config.
   * @private
   * @memberof BfxHedgeOrderMonitorComponent
   */
  private subscribeToAuth(): void {
    // this.authSubscription = this.authService.authStatus$.subscribe(
    // 	(status: boolean) => {
    // 		if (!status) { // user is logged out
    // 			this.bfxHedgeOrderMonitorService.saveConfigToSession(this.blotterId, this.getBlotterColumnsConfigs());
    // 			// this.bfxHedgeOrderMonitorService.setStagingBlotterColumnsConfig(this.getBlotterColumnsConfigs());
    // 		}
    // 	}
    // );
  }

  /**
   * @description Gets triggered when a user saves a profile, and overwrites profile config.
   * @private
   * @memberof BfxHedgeOrderMonitorComponent
   */
  private subscribeToSaveProfile(): void {
    this.saveProfileSubscription = this.layoutService.savedProfileAction$.subscribe(
      (isSaved: boolean) => {
        if (isSaved) {
          // this.bfxHedgeOrderMonitorService.setStagingBlotterColumnsConfig(this.getBlotterColumnsConfigs());
          this.bfxHedgeOrderMonitorService.saveConfigToSession(
            this.blotterId,
            this.getBlotterColumnsConfigs(),
          );
        }
      },
    );
  }

  public showColumnSelector(event): void {
    if (event.which == 3) {
      let targetClass: string = event.target.className;
      if (
        targetClass === "ag-header-cell" ||
        targetClass === "ag-header-cell-text" ||
        targetClass === "ag-header-cell-label"
      ) {
        this.columnSelectorService.doColumnSelectorMenu(this, this.gridOptions, event);
      }
    }
  }

  public onCellDoubleClicked(event) {
    // binding for template. please overide in subclass
  }

  public getBlotterColumnsConfigs(): any {
    if (
      this.gridOptions == null ||
      this.gridOptions.columnApi == null ||
      typeof this.gridOptions.columnApi.getAllGridColumns !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // column config won't be restored but defaults will still apply
      return null;
    }
    let columnsToSave: any = {};
    let gridColumns: Column[] = this.gridOptions.columnApi.getAllGridColumns();
    let positionCounter: number = 0;
    gridColumns.forEach((gridCol: Column) => {
      let colDef: BfxColDef = gridCol.getColDef();

      columnsToSave[colDef.field] = Object.assign(
        {},
        {
          field: colDef.field,
          headerName: colDef.headerName,
          hide: !gridCol.isVisible(),
          position: positionCounter++,
          disableInPicker: colDef.disableInPicker,
          width: gridCol.getActualWidth(),
        },
      );
    });
    columnsToSave.hasBulkEditRow = this.hasBulkEditRow();
    return columnsToSave;
  }

  public getBlotterSortingModel() {
    if (
      this.gridOptions == null ||
      this.gridOptions.api == null ||
      typeof this.gridOptions.api.getSortModel !== "function"
    ) {
      logger.out.error(
        `${this.constructor.name}.saveAsDefault() failed ag-grid gridOptions are invalid`,
      );
      // sort model won't be restored but defaults will still apply
      return null;
    }

    return this.gridOptions.api.getSortModel();
  }

  public getBlotterId(): string {
    return this.blotterId;
  }

  public getRowCount(): number {
    return Object.values(this.data).length;
  }

  public addRows(rows: BfxHedgeOrderStagingRow[]): Promise<RowNode[]> {
    rows.forEach((row: BfxHedgeOrderStagingRow) => {
      this.data[row.id] = row;
      // bulk row update
      this.determineAndApplyBulkRowInitialValues();
    });

    return new Promise<RowNode[]>(
      ((resolve) => {
        this.refresh().then(
          (() => {
            resolve(
              rows.map((row: BfxHedgeOrderStagingRow) => this.gridOptions.api.getRowNode(row.id)),
            );
          }).bind(this),
        );
      }).bind(this),
    );
  }

  public removeRows(rows: Array<BfxHedgeOrderStagingRow>): Promise<void> {
    rows.forEach((row: BfxHedgeOrderStagingRow) => {
      delete this.data[row.id];
    });
    // bulk row update
    this.determineAndApplyBulkRowInitialValues();

    return this.refresh();
  }

  public updateRow(row: BfxHedgeOrderStagingRow): void {
    if (this.tableDefinition != null && this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.updateRowData({
        update: [row],
      });
      this.determineAndApplyBulkRowInitialValues();
    }
  }

  protected refresh(): Promise<void> {
    return new Promise<void>(
      ((resolve) => {
        setTimeout(
          (() => {
            let values: Array<BfxHedgeOrderStagingRow> = Object.values(this.data);
            values.sort((item) => (item.bulkEditRow ? -1 : 0));

            let unsubmittedCount: number = this.getUnsubmittedOrderCount();
            let deltaCount: number = unsubmittedCount - this.stagingOrderCount;
            this.bfxHedgeOrderMonitorService.updateStagingOrderCount(deltaCount);
            this.stagingOrderCount = unsubmittedCount;

            if (this.tableDefinition != null && this.gridOptions && this.gridOptions.api) {
              this.gridOptions.api.setRowData(values);
              this.gridOptions.api.refreshCells();
              this.selectionChangedSubject.next(this.gridOptions.api.getSelectedNodes());
            } else {
              logger.out.warn(`${this.constructor.name}.refreshDataInGrid() failed`);
            }
            resolve();
          }).bind(this),
          0,
        );
      }).bind(this),
    );
  }

  public getAutoCompleteValues(params: any): Array<any> {
    let values: Array<any> = [];

    switch (params.colDef.field) {
      case COLUMN_TRADER_GROUP_NAMES:
        values = this.traderGroupLookupValues;
        break;
      case COLUMN_ACCOUNT_ID:
        values = this.accountLookupValues;
        break;
      case COLUMN_CCY_PAIR_CODE:
        values = this.currencyPairLookupValues;
        break;
      case COLUMN_ORDER_TYPE:
        let traderGroup: CubiqTS.TradingGroupImpl = params.data.traderGroup;
        if (traderGroup != null) {
          if (!traderGroup.isSupportsBMAlgosFg) {
            values = MANUAL_EXTERNAL_ORDER_TYPES;
          } else {
            values = this.orderTypeLookupValues[traderGroup.groupCode];
          }
        } else {
          values = [];
        }
        break;
    }
    // In case some of our lookups are not initialised - e.g. no Algo profiles defined
    if (!Array.isArray(values)) {
      logger.out.warn(`HOM Grid: Cannot find lookup values for ${params.colDef.field}`);
      values = [];
    }

    return values;
  }

  public getAutoCompleteSelectedValue(params: any): any {
    let value: any = [];
    let data: BfxHedgeOrderStagingRow = params.data;

    switch (params.colDef.field) {
      case COLUMN_TRADER_GROUP_NAMES:
        value = this.traderGroupLookupValues.find(
          (a: any) => a.label == data.orderRequest.traderGroup,
        );
        break;
      case COLUMN_ACCOUNT_ID:
        value = this.accountLookupValues.find((a: any) => a.value == data.orderRequest.accountCode);
        break;
      case COLUMN_CCY_PAIR_CODE:
        value = this.currencyPairLookupValues.find(
          (a: any) => a.value == data.orderRequest.base + data.orderRequest.nonBase,
        );
        break;
      case COLUMN_ORDER_TYPE:
        {
          let traderGroup: CubiqTS.TradingGroupImpl = params.data.traderGroup;
          if (traderGroup != null) {
            if (!traderGroup.isSupportsBMAlgosFg) {
              value = MANUAL_EXTERNAL_ORDER_TYPES[0];
            } else if (data.algoHedgeConfig != null) {
              value = this.orderTypeLookupValues[traderGroup.groupCode].find(
                (a: any) => a.value == data.algoHedgeConfig.algoHedgeId.toString(),
              );
            }
          }
        }
        break;
    }

    return value;
  }

  public setEditedValue(value: any, params: any): void {
    switch (params.colDef.field) {
      case COLUMN_TRADER_GROUP_NAMES:
        if (value.value && value.label) {
          this.setTraderGroup(value, params);
          if (params.data.bulkEditRow) {
            this.applyBulkValue(COLUMN_TRADER_GROUP_NAMES, value);
          } else if (this.hasBulkEditRow()) {
            this.determineAndApplyBulkRowInitialValues(this.getBulkEditRow());
          }
        }
        break;
      case COLUMN_ACCOUNT_ID:
        this.setAccount(value, params);
        if (params.data.bulkEditRow) {
          this.applyBulkValue(COLUMN_ACCOUNT_ID, value);
        } else if (this.hasBulkEditRow()) {
          this.determineAndApplyBulkRowInitialValues(this.getBulkEditRow());
        }
        break;
      case COLUMN_CCY_PAIR_CODE:
        if (value.value) {
          let base: string = value.value.substring(0, 3),
            nonBase = value.value.substring(3);
          params.data.orderRequest.base = base;
          params.data.orderRequest.nonBase = nonBase;
          params.data.orderRequest.fixedCcy = base;
        }
        break;
      case COLUMN_DEALT_DIR_DATA:
        params.data.orderRequest.clientIntent = value;
        break;
      case COLUMN_ORDER_AMOUNT_DATA:
        // WIll never be called - setting in BfxHedgeOrderStagingRow
        // will update the orderRequest
        params.data.orderRequest.fixedAmount = value.toString();
        break;
      case COLUMN_START_TIME:
        this.setStartTime(value, params);
        if (params.data.bulkEditRow) {
          this.applyBulkValue(COLUMN_START_TIME, value);
        } else if (this.hasBulkEditRow()) {
          this.determineAndApplyBulkRowInitialValues(this.getBulkEditRow());
        }
        break;
      case COLUMN_END_TIME:
        this.setEndTime(value, params);
        if (params.data.bulkEditRow) {
          this.applyBulkValue(COLUMN_END_TIME, value);
        } else if (this.hasBulkEditRow()) {
          this.determineAndApplyBulkRowInitialValues(this.getBulkEditRow());
        }
        break;
      case COLUMN_ORDER_TYPE:
        this.setOrderType(value, params);
        if (params.data.bulkEditRow) {
          this.applyBulkValue(COLUMN_ORDER_TYPE, value);
        } else if (this.hasBulkEditRow()) {
          this.determineAndApplyBulkRowInitialValues(this.getBulkEditRow());
        }

        break;
      case COLUMN_PRIVATE_COMMENT:
        this.setPrivateComment(value, params);
        if (params.data.bulkEditRow && !(value == null && params.value == null)) {
          this.applyBulkValue(COLUMN_PRIVATE_COMMENT, value);
        } else if (this.hasBulkEditRow()) {
          this.determineAndApplyBulkRowInitialValues(this.getBulkEditRow());
        }
        break;
    }
  }

  public isCellEditingEnabled(params: any): boolean {
    return this.hedgeOrdersStagingTableDefinition.isEditable(params);
  }

  public shouldRoundToMillions(params: any): CubiqTS.RoundToMillionsType {
    let data: BfxHedgeOrderStagingRow = params.data;
    return data != null ? data.roundToMillion() : null;
  }

  public createNewOrder(): void {
    let orderRequest: CubiqTS.FixingOrderRequestDataImpl = new CubiqTS.FixingOrderRequestDataImpl();
    let fixingTime: CubiqTS.Moment = CubiqTS.moment.utc(this.fixingData.fixingTimestamp);

    orderRequest.fixedAmount = "0";
    orderRequest.fixingSource = this.fixingData.fixingDef.code;
    orderRequest.clientIntent = CubiqTS.OrderIntent.BUY;
    orderRequest.fixingTime = new CubiqTS.StandardTimeImpl();
    orderRequest.fixingTime.hours = fixingTime.hours();
    orderRequest.fixingTime.minutes = fixingTime.minutes();
    orderRequest.fixingTime.seconds = fixingTime.seconds();
    orderRequest.fixingTime.timeZone = "UTC";
    orderRequest.fixingDate = new CubiqTS.StandardDateImpl();
    orderRequest.fixingDate.day = fixingTime.date();
    orderRequest.fixingDate.month = fixingTime.month() + 1;
    orderRequest.fixingDate.year = fixingTime.year();
    orderRequest.product = CubiqTS.Product.SPT;

    let row: BfxHedgeOrderStagingRow = new BfxHedgeOrderStagingRow(orderRequest);
    row.locked = false;

    this.addRows([row]);
  }

  public copySelected(): void {
    if (this.gridOptions && this.gridOptions.api) {
      let selectedRows: Array<BfxHedgeOrderStagingRow> = this.gridOptions.api.getSelectedRows();
      if (selectedRows != null && selectedRows.length > 0) {
        this.addRows(selectedRows.map((row) => row.clone())).then((rows: RowNode[]) =>
          rows.forEach((row: RowNode) => row.setSelected(true)),
        );
      }
    }
  }

  public submitSelected(): void {
    if (this.gridOptions && this.gridOptions.api) {
      this.gridOptions.api.stopEditing();

      let selectedRows: Array<BfxHedgeOrderStagingRow> = this.gridOptions.api
        .getSelectedRows()
        .filter((row: BfxHedgeOrderStagingRow) => !row.processing && !row.bulkEditRow);
      let validRows: BfxHedgeOrderStagingRow[] = selectedRows.filter(this.validateRow);

      if (validRows.length == 0) {
        this.showFailDialog();
      }

      validRows.forEach(this.submitOrder.bind(this));
    }
    this.refresh();
  }

  protected validateRow(row: BfxHedgeOrderStagingRow): boolean {
    row.submitted = false;

    let errors: any = [];

    if (row.traderGroup == null) {
      errors.push("hedge.validation.invalidTraderGroup");
    }

    if (row.orderRequest.base == null) {
      errors.push("hedge.validation.invalidCurrencyPair");
    }

    if (row.orderRequest.clientIntent == null) {
      errors.push("hedge.validation.invalidIntent");
    }

    if (
      row.orderRequest.orderType == null ||
      (row.orderRequest.orderType == CubiqTS.OrderType.BMALGO && row.algoHedgeConfig == null)
    ) {
      errors.push("hedge.validation.invalidOrderType");
    }

    if (row.orderRequest.accountCode == null) {
      errors.push("hedge.validation.invalidAccount");
    }

    if (
      row.orderRequest.fixedAmount == null ||
      new CubiqTS.BigNumber(row.orderRequest.fixedAmount).isZero()
    ) {
      errors.push("hedge.validation.invalidAmount");
    }

    row.errors = errors.length > 0 ? errors : null;

    return errors.length == 0;
  }

  public hasUnsubmittedOrders(): boolean {
    return this.getUnsubmittedOrderCount() > 0;
  }

  public getUnsubmittedOrderCount(): number {
    return Object.values(this.data).filter(
      (order: BfxHedgeOrderStagingRow) =>
        !order.processing && !order.submitted && !order.bulkEditRow,
    ).length;
  }

  protected submitOrder(row: BfxHedgeOrderStagingRow): void {
    row.processing = row.submitted = true;

    if (row.isAlgo()) {
      let request: CubiqTS.LinkedOrderRequestImpl = this.createAlgoRequest(row);
      this.orderDataActionService
        .submitNewOrder(request)
        .then(this.processResponse.bind(this, row));
    } else {
      let request: CubiqTS.FixingOrderRequestImpl = new CubiqTS.FixingOrderRequestImpl();
      request.fixingOrderData = row.orderRequest;
      this.orderDataActionService
        .submitBmarkOrder(request)
        .then(this.processResponse.bind(this, row));
    }
  }

  protected processResponse(row: BfxHedgeOrderStagingRow, response: any): void {
    if (response instanceof CubiqTS.OrderLifeCycleEventImpl) {
      response = (response as CubiqTS.OrderLifeCycleEventImpl).errors;
    }

    if (response instanceof CubiqTS.ErrorsImpl) {
      try {
        let errors: CubiqTS.ErrorsImpl = response as CubiqTS.ErrorsImpl;
        row.errors = errors.errorMessages
          .map((error: any) => JSON.parse(error))
          .map((error: any) =>
            this.translateService.instant(error.id, this.processErrorArgs(error.args)),
          );
      } catch (error) {
        logger.out.error("Failed to parse submit hedge order response " + response + " " + error);
      } finally {
        row.processing = false;
      }
    }

    this.refresh();
  }

  protected processErrorArgs(errorArgs: Array<any>): Array<any> {
    try {
      if (errorArgs != null && errorArgs.length > 0) {
        errorArgs = errorArgs.map((errorArg: any) => {
          if (errorArg != null && errorArg.time) {
            return CubiqTS.moment(errorArg.time).format(ERROR_ARGS_TIME_FORMAT);
          }
          return errorArg;
        });
      }
    } catch (error) {
      logger.out.warn("Failed to parse error arguments on rejected hedge order " + error);
    }

    return errorArgs;
  }

  protected processOrderLifecycleEvent(event: CubiqTS.OrderLifeCycleEventImpl): void {
    try {
      if (
        event.orderData != null &&
        event.orderData.clOrdID != null &&
        (event.orderData.orderType == CubiqTS.OrderType.BENCHMARK ||
          event.orderData.orderType == CubiqTS.OrderType.BMALGO)
      ) {
        let row: BfxHedgeOrderStagingRow = (
          Object.values(this.data) as BfxHedgeOrderStagingRow[]
        ).find(
          (row: BfxHedgeOrderStagingRow) =>
            row.orderRequest.externalClientReference == event.orderData.clOrdID,
        );

        if (row != null) {
          let orderState: CubiqTS.OrderState = event.orderData.orderState;
          row.orderState = this.translateService.instant(
            "order.state." + CubiqTS.OrderState[orderState],
          );
          if (orderState == CubiqTS.OrderState.REJECTED) {
            row.orderRequest.externalClientReference = uuid();
            this.processResponse(row, event);
          } else if (
            orderState != CubiqTS.OrderState.ACCEPTANCE_NEEDED &&
            orderState != CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED
          ) {
            this.removeRows([row]);
          } else {
            row.errors = null;
            this.refresh();
          }
        }
      }
    } catch (error) {
      logger.out.error("Error processing order lifecycle event: " + event + " " + error);
    }
  }

  protected createAlgoRequest(row: BfxHedgeOrderStagingRow): CubiqTS.LinkedOrderRequestImpl {
    let request: CubiqTS.LinkedOrderRequestImpl = new CubiqTS.LinkedOrderRequestImpl();
    request.account = row.orderRequest.accountCode;
    request.client = row.orderRequest.clientCode;
    request.comment2 = row.orderRequest.comment2;
    request.product = CubiqTS.Product.SPT;

    let requestData: CubiqTS.OrderRequestDataImpl = new CubiqTS.OrderRequestDataImpl();
    requestData.accountCode = request.account;
    requestData.clientCode = request.client;
    requestData.base = row.orderRequest.base;
    requestData.nonBase = row.orderRequest.nonBase;
    requestData.clientIntent = row.orderRequest.clientIntent;
    requestData.comment2 = row.orderRequest.comment2;
    requestData.fixedAmount = row.orderRequest.fixedAmount.toString();
    requestData.fixedCcy = row.orderRequest.base;
    requestData.orderType = row.orderRequest.orderType;
    requestData.requestOrderType = row.orderRequest.requestOrderType;
    requestData.product = CubiqTS.Product.SPT;
    requestData.fixingCode = row.orderRequest.fixingSource;
    requestData.fixingDate = row.orderRequest.fixingDate;
    requestData.fixingTime = row.orderRequest.fixingTime;
    requestData.traderGroup = row.traderGroup.groupCode;
    requestData.algoProfileId = row.algoHedgeConfig.algoProfile;
    request.linkReference = requestData.externalClientReference =
      row.orderRequest.externalClientReference;

    if (row.orderRequest.strategyParameters != null) {
      requestData.strategyParameters = row.orderRequest.strategyParameters.slice(0);
    } else {
      requestData.strategyParameters = [];
    }

    requestData.activationDetailUtc = row.startTime;
    requestData.expiryDetailUtc = row.endTime;

    request.rootOrder = requestData;

    return request;
  }

  public deleteSelected(): void {
    if (this.gridOptions && this.gridOptions.api) {
      let selectedRows: Array<BfxHedgeOrderStagingRow> = this.gridOptions.api.getSelectedRows();
      if (selectedRows != null && selectedRows.length == 1) {
        this.removeRows(selectedRows.filter((row: BfxHedgeOrderStagingRow) => !row.processing));
      } else if (selectedRows != null && selectedRows.length > 1) {
        this.showConfirmDeleteDialog();
      }
    }
  }

  protected onConfirmDelete(): void {
    if (this.gridOptions && this.gridOptions.api) {
      let selectedRows: Array<BfxHedgeOrderStagingRow> = this.gridOptions.api.getSelectedRows();
      if (selectedRows != null && selectedRows.length > 0) {
        this.removeRows(selectedRows.filter((row: BfxHedgeOrderStagingRow) => !row.processing));
      }
    }
  }

  protected onTraderGroupChanged(
    row: BfxHedgeOrderStagingRow,
    oldTraderGroup: CubiqTS.TradingGroupImpl,
    newTraderGroup: CubiqTS.TradingGroupImpl,
  ): void {
    let startTime: CubiqTS.Moment = null;
    let endTime: CubiqTS.Moment = null;
    let algoHedgeConfig: CubiqTS.AlgoHedgeConfigInfoImpl = null;
    let orderRequestData: CubiqTS.FixingOrderRequestDataImpl = row.orderRequest;

    if (newTraderGroup.isSupportsBMAlgosFg) {
      orderRequestData.orderType = CubiqTS.OrderType.BMALGO;
      orderRequestData.requestOrderType = CubiqTS.RequestOrderType.BMALGO;
    } else {
      orderRequestData.orderType = CubiqTS.OrderType.BENCHMARK;
      orderRequestData.requestOrderType = CubiqTS.RequestOrderType.BMARK;
      orderRequestData.strategyParameters = null;
    }

    orderRequestData.fixedAmount = this.hedgeOrderMonitorUtilService
      .roundToMillion(orderRequestData.fixedAmount, newTraderGroup)
      .toString();

    let defaults: CubiqTS.DefaultAccountProfileMappingImpl[] = this.defaultAccountMappings;
    if (defaults != null && defaults.length > 0) {
      let accountDefault: CubiqTS.DefaultAccountProfileMappingImpl = defaults.find((def) =>
        def.traderGroupId.equals(newTraderGroup.groupId),
      );

      if (accountDefault != null) {
        let account: CubiqTS.AccountImpl = this.accountLookupService.getAccount(
          accountDefault.defaultAccountCd,
        );

        if (account != null) {
          let oldAccountCode: string = orderRequestData.accountCode;
          if (oldAccountCode != accountDefault.defaultAccountCd) {
            orderRequestData.accountCode = accountDefault.defaultAccountCd;
            this.onAccountChanged(row, oldAccountCode, orderRequestData.accountCode);
          }
        }

        let fixingTime: number = this.fixingData.fixingTimestamp;
        if (accountDefault.defaultAlgoHedgingProfileId != null) {
          let profileId: string = accountDefault.defaultAlgoHedgingProfileId.toString();
          algoHedgeConfig = this.algoHedgeService.getItem(profileId);

          if (algoHedgeConfig != null && algoHedgeConfig.enabledFg) {
            if (algoHedgeConfig.startTimeOffset != null) {
              startTime = CubiqTS.moment(
                fixingTime.valueOf() - algoHedgeConfig.startTimeOffset.toNumber() * 1000,
              );
            }

            if (algoHedgeConfig.endTimeOffset != null) {
              endTime = CubiqTS.moment(
                fixingTime.valueOf() + algoHedgeConfig.endTimeOffset.toNumber() * 1000,
              );
            }

            orderRequestData.strategyParameters = algoHedgeConfig.strategyParams.slice(0);
          } else {
            algoHedgeConfig = null;
            logger.out.warn(
              "Could not load missing algo hedge config ",
              profileId,
              " from profile ",
              accountDefault.defaultAlgoHedgingProfileId,
            );
          }
        }
      }
    }

    row.startTime = startTime;
    row.endTime = endTime;
    row.algoHedgeConfig = algoHedgeConfig;
  }

  protected onAccountChanged(
    row: BfxHedgeOrderStagingRow,
    oldAccountCode: string,
    newAccountCode: string,
  ): void {
    let account: CubiqTS.AccountImpl = this.accountLookupService.getAccount(newAccountCode);
    if (account != null) {
      row.orderRequest.clientCode = account.clientId;
      let client: CubiqTS.ClientImpl = this.clientLookupService.getClient(account.clientId);
      if (client != null && client.defaultPrivateComment != null) {
        row.orderRequest.comment2 = client.defaultPrivateComment;
        // added to match flex behavior, might be different in HTML check with KD and remove if necessary
      } else if (oldAccountCode !== newAccountCode) {
        row.orderRequest.comment2 = null;
      }
    }
  }

  protected showConfirmDeleteDialog(): void {
    let payload = {
      messages: [this.translateService.instant("hedge.order.monitor.staging.delete.confirm")],
      title: "hedge.order.monitor.staging.delete.confirm.title",
      showConfirmationMessage: false,
      showLogoutMessage: false,
      callback: this.onConfirmDelete.bind(this),
    };

    let bfxDialogData: BfxDialogData = new BfxDialogData(
      BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
      null,
      null,
      payload,
    );

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.element.nativeElement);
      bfxDialogData.body = wnd.getNativeWindow().document.body;
    }

    BfxHedgeOrderMonitorComponent.addDialog(bfxDialogData);
  }

  protected showFailDialog(): void {
    let payload = {
      messages: [this.translateService.instant("hedge.order.monitor.staging.validation.fail")],
      title: "hedge.order.monitor.staging.validation.fail.title",
      showConfirmationMessage: false,
      showLogoutMessage: false,
    };

    let bfxDialogData: BfxDialogData = new BfxDialogData(
      BfxDialogCommand.DIALOG_GENERIC_OK,
      null,
      null,
      payload,
    );

    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.element.nativeElement);
      bfxDialogData.body = wnd.getNativeWindow().document.body;
    }

    BfxHedgeOrderMonitorComponent.addDialog(bfxDialogData);
  }

  public editStrategyParameters(orderId: string): void {
    let row: BfxHedgeOrderStagingRow = this.data[orderId];

    if (
      row != null &&
      row.algoHedgeConfig != null &&
      row.algoHedgeConfig.orderType != null &&
      !row.processing
    ) {
      let config: BfxHedgeOrderStrategyParamsConfig = new BfxHedgeOrderStrategyParamsConfig();
      config.algoType = new ICAutocompleteGenericModel(
        row.algoHedgeConfig.orderType,
        row.algoHedgeConfig.orderType,
      );
      config.orderAmount = row.orderRequest.fixedAmount;

      if (row.orderRequest.base != null) {
        config.currencyCode = row.orderRequest.base;
        config.currencyPair = row.orderRequest.base + row.orderRequest.nonBase;
        let rate: Rate = this.rateCache.getRate(config.currencyPair);
        if (
          rate != null &&
          rate.bidRate != null &&
          rate.offerRate != null &&
          !new CubiqTS.BigNumber(rate.bidRate).plus(rate.offerRate).isZero()
        ) {
          config.rate = new CubiqTS.BigNumber(
            new CubiqTS.BigNumber(rate.bidRate).plus(new CubiqTS.BigNumber(rate.offerRate)),
          ).dividedBy(2);
        }
      }

      let payload: any = {
        data: row,
        config: config,
        callback: this.saveStrategyParameters.bind(this),
      };

      let bfxDialogData: BfxDialogData = new BfxDialogData(
        BfxDialogCommand.DIALOG_GENERIC_CONFIRM_OPEN,
        null,
        null,
        payload,
      );

      if (NativeDesktopSupport.isNativeDesktopContainer()) {
        let wnd: NativeDesktopWindow = NativeDesktopSupport.getWindow(this.element.nativeElement);
        bfxDialogData.body = wnd.getNativeWindow().document.body;
      }

      this.strategyParametersDialog.openDialog(bfxDialogData);
      this.strategyParametersDialog.postOpenDialog(bfxDialogData);
    }
  }

  public saveStrategyParameters(): void {
    this.refresh();
  }

  /**********************************************************************/
  /* Section: BULK-EDIT-ROW */

  /***************************************/
  /* BULK-EDIT-ROW -> ADD/REMOVE BULK ROW*/

  private hasBulkEditRow(): boolean {
    return this.bulkRowId != null && this.data[this.bulkRowId] instanceof BfxHedgeOrderStagingRow;
  }

  private getBulkEditRow(): BfxHedgeOrderStagingRow | null {
    let bulkEditRow = this.data[this.bulkRowId];
    return bulkEditRow instanceof BfxHedgeOrderStagingRow ? bulkEditRow : null;
  }

  public addBulkEditRow(): void {
    if (!this.hasBulkEditRow() && this.fixingData != null) {
      let orderRequest: CubiqTS.FixingOrderRequestDataImpl =
        new CubiqTS.FixingOrderRequestDataImpl();
      let fixingTime: CubiqTS.Moment = CubiqTS.moment.utc(this.fixingData.fixingTimestamp);

      orderRequest.fixedAmount = "0";
      orderRequest.fixingSource = this.fixingData.fixingDef.code;
      orderRequest.clientIntent = CubiqTS.OrderIntent.BUY;
      orderRequest.fixingTime = new CubiqTS.StandardTimeImpl();
      orderRequest.fixingTime.hours = fixingTime.hours();
      orderRequest.fixingTime.minutes = fixingTime.minutes();
      orderRequest.fixingTime.seconds = fixingTime.seconds();
      orderRequest.fixingTime.timeZone = "UTC";
      orderRequest.fixingDate = new CubiqTS.StandardDateImpl();
      orderRequest.fixingDate.day = fixingTime.date();
      orderRequest.fixingDate.month = fixingTime.month() + 1;
      orderRequest.fixingDate.year = fixingTime.year();
      orderRequest.product = CubiqTS.Product.SPT;

      let row: BfxHedgeOrderStagingRow = new BfxHedgeOrderStagingRow(orderRequest);
      row.bulkEditRow = true;
      row.locked = false;
      this.determineAndApplyBulkRowInitialValues(row);
      this.bulkRowId = row.id;

      this.addRows([row]);
    } else {
      logger.out.warn(`Bulk edit row already present`);
    }
  }

  public removeBulkEditRow(): void {
    let bulkEditRow = this.getBulkEditRow();
    if (bulkEditRow instanceof BfxHedgeOrderStagingRow) {
      this.removeRows([bulkEditRow]);
      this.bulkRowId = null;
    }
  }

  /***************************************/
  /* BULK-EDIT-ROW -> VALUE LOAD */

  private determineAndApplyBulkRowInitialValues(bulkRow?: BfxHedgeOrderStagingRow): void {
    if (bulkRow == null && this.hasBulkEditRow()) {
      bulkRow = this.getBulkEditRow();
    }
    if (bulkRow instanceof BfxHedgeOrderStagingRow && Object.keys(this.data).length > 0) {
      let loadData: {
        traderGroup: {
          value: CubiqTS.TradingGroupImpl;
          setToNull: boolean;
        };
        orderType: {
          value: CubiqTS.OrderType;
          requestValue: CubiqTS.RequestOrderType;
          algoHedgeConfig: CubiqTS.AlgoHedgeConfigInfoImpl | null;
          setToNull: boolean;
        };
        account: {
          value: string;
          setToNull: boolean;
        };
        comment: {
          value: string;
          setToNull: boolean;
        };
        startTime: {
          value: any;
          setToNull: boolean;
        };
        endTime: {
          value: any;
          setToNull: boolean;
        };
      } = {
        traderGroup: {
          value: null,
          setToNull: false,
        },
        orderType: {
          value: null,
          requestValue: null,
          algoHedgeConfig: null,
          setToNull: false,
        },
        account: {
          value: null,
          setToNull: false,
        },
        comment: {
          value: null,
          setToNull: false,
        },
        startTime: {
          value: null,
          setToNull: false,
        },
        endTime: {
          value: null,
          setToNull: false,
        },
      };

      let dataRowCount: number = 0;

      // DETERMINE
      for (let id in this.data) {
        let rowData = this.data[id];
        if (rowData instanceof BfxHedgeOrderStagingRow && !rowData.bulkEditRow) {
          let orderRequest: CubiqTS.FixingOrderRequestDataImpl = rowData.orderRequest;
          dataRowCount++;

          // Trader Group
          if (rowData.traderGroup == null) {
            loadData.traderGroup.setToNull = true;
          } else if (
            loadData.traderGroup.value == null &&
            rowData.traderGroup instanceof CubiqTS.TradingGroupImpl
          ) {
            loadData.traderGroup.value = rowData.traderGroup;
          } else if (
            loadData.traderGroup.value instanceof CubiqTS.TradingGroupImpl &&
            rowData.traderGroup instanceof CubiqTS.TradingGroupImpl &&
            !loadData.traderGroup.value.groupId.equals(rowData.traderGroup.groupId)
          ) {
            loadData.traderGroup.setToNull = true;
          }
          // Order Type
          if (orderRequest.orderType == null) {
            loadData.orderType.setToNull = true;
          } else if (
            loadData.orderType.value == null &&
            CubiqTS.OrderType.getAllElements().includes(orderRequest.orderType)
          ) {
            loadData.orderType.value = orderRequest.orderType;
            loadData.orderType.requestValue = orderRequest.requestOrderType;
            if (orderRequest.orderType === CubiqTS.OrderType.BENCHMARK) {
              loadData.orderType.algoHedgeConfig = null;
            } else {
              loadData.orderType.algoHedgeConfig = rowData.algoHedgeConfig;
            }
          } else if (
            CubiqTS.OrderType.getAllElements().includes(loadData.orderType.value) &&
            CubiqTS.OrderType.getAllElements().includes(orderRequest.orderType) &&
            (loadData.orderType.value != orderRequest.orderType ||
              (loadData.orderType.value === CubiqTS.OrderType.BMALGO &&
                loadData.orderType.algoHedgeConfig != null &&
                (rowData.algoHedgeConfig == null ||
                  loadData.orderType.algoHedgeConfig.algoProfile !=
                    rowData.algoHedgeConfig.algoProfile)))
          ) {
            loadData.orderType.setToNull = true;
            if (loadData.orderType.value != orderRequest.orderType) {
              loadData.orderType.value = null;
              loadData.orderType.requestValue == null;
            } else {
              loadData.orderType.value = orderRequest.orderType;
              loadData.orderType.requestValue = orderRequest.requestOrderType;
            }
            loadData.orderType.algoHedgeConfig = null;
          }
          // Account
          if (orderRequest.accountCode == null) {
            loadData.account.setToNull = true;
          } else if (
            loadData.account.value == null &&
            typeof orderRequest.accountCode === "string"
          ) {
            loadData.account.value = orderRequest.accountCode;
          } else if (
            typeof loadData.account.value === "string" &&
            typeof orderRequest.accountCode === "string" &&
            loadData.account.value !== orderRequest.accountCode
          ) {
            loadData.account.setToNull = true;
          }
          // Private Comment
          if (orderRequest.comment2 == null) {
            loadData.comment.setToNull = true;
          } else if (loadData.comment.value == null && typeof orderRequest.comment2 === "string") {
            loadData.comment.value = orderRequest.comment2;
          } else if (
            typeof loadData.comment.value === "string" &&
            typeof orderRequest.comment2 === "string" &&
            loadData.comment.value !== orderRequest.comment2
          ) {
            loadData.comment.setToNull = true;
          }
          // Start Time
          if (rowData.startTime == null) {
            loadData.startTime.setToNull = true;
          } else if (
            loadData.startTime.value == null &&
            CubiqTS.moment.isMoment(rowData.startTime)
          ) {
            loadData.startTime.value = rowData.startTime;
          } else if (
            CubiqTS.moment.isMoment(loadData.startTime.value) &&
            CubiqTS.moment.isMoment(rowData.startTime) &&
            rowData.startTime.format("YYYYMMDD HH:mm:ss") !=
              loadData.startTime.value.format("YYYYMMDD HH:mm:ss")
          ) {
            loadData.startTime.setToNull = true;
          }
          // End Time
          if (rowData.endTime == null) {
            loadData.endTime.setToNull = true;
          } else if (loadData.endTime.value == null && CubiqTS.moment.isMoment(rowData.endTime)) {
            loadData.endTime.value = rowData.endTime;
          } else if (
            CubiqTS.moment.isMoment(loadData.endTime.value) &&
            CubiqTS.moment.isMoment(rowData.endTime) &&
            rowData.endTime.format("YYYYMMDD HH:mm:ss") !=
              loadData.endTime.value.format("YYYYMMDD HH:mm:ss")
          ) {
            loadData.endTime.setToNull = true;
          }
        }
      }

      // If there are no rows set everything to null
      if (dataRowCount == 0) {
        loadData.traderGroup.setToNull = true;
        loadData.account.setToNull = true;
        loadData.comment.setToNull = true;
        loadData.endTime.setToNull = true;
        loadData.orderType.setToNull = true;
        loadData.startTime.setToNull = true;
      }

      // APPLY
      // Trader Group
      if (
        loadData.traderGroup.value instanceof CubiqTS.TradingGroupImpl &&
        !loadData.traderGroup.setToNull
      ) {
        bulkRow.orderRequest.traderGroup = loadData.traderGroup.value.groupCode;
        bulkRow.traderGroup = loadData.traderGroup.value;
      } else if (loadData.traderGroup.setToNull) {
        bulkRow.orderRequest.traderGroup = null;
        bulkRow.traderGroup = null;
      }
      // Order Type
      if (loadData.orderType.value != null && !loadData.orderType.setToNull) {
        bulkRow.orderRequest.orderType = loadData.orderType.value;
        bulkRow.orderRequest.requestOrderType = loadData.orderType.requestValue;
        bulkRow.algoHedgeConfig = loadData.orderType.algoHedgeConfig;
      } else {
        bulkRow.orderRequest.orderType = null; //loadData.orderType.value; // OMS-35656
        bulkRow.orderRequest.requestOrderType = loadData.orderType.requestValue;
        bulkRow.algoHedgeConfig = loadData.orderType.algoHedgeConfig;
      }
      // Account
      if (typeof loadData.account.value === "string" && !loadData.account.setToNull) {
        bulkRow.orderRequest.accountCode = loadData.account.value;
      } else if (loadData.account.setToNull) {
        bulkRow.orderRequest.accountCode = null;
      }
      // Private Comment
      if (typeof loadData.comment.value === "string" && !loadData.comment.setToNull) {
        bulkRow.orderRequest.comment2 = loadData.comment.value;
      } else if (loadData.comment.setToNull) {
        bulkRow.orderRequest.comment2 = null;
      }
      // Start Time
      if (CubiqTS.moment.isMoment(loadData.startTime.value) && !loadData.startTime.setToNull) {
        bulkRow.startTime = loadData.startTime.value;
      } else if (loadData.startTime.setToNull) {
        bulkRow.startTime = null;
      }
      // End Time
      if (CubiqTS.moment.isMoment(loadData.endTime.value) && !loadData.endTime.setToNull) {
        bulkRow.endTime = loadData.endTime.value;
      } else if (loadData.endTime.setToNull) {
        bulkRow.endTime = null;
      }
    }
  }

  /***************************************/
  /* BULK-EDIT-ROW -> BULK VALUE APPLICATION */

  private applyBulkValue(property: string, value: any): void {
    if (this.data != null && Object.keys(this.data).length > 0 && typeof property === "string") {
      for (let id in this.data) {
        let rowData = this.data[id];
        if (rowData instanceof BfxHedgeOrderStagingRow && !rowData.bulkEditRow) {
          switch (property) {
            case COLUMN_TRADER_GROUP_NAMES:
              this.setTraderGroup(value, rowData);
              break;
            case COLUMN_ACCOUNT_ID:
              this.setAccount(value, rowData);
              break;
            case COLUMN_ORDER_TYPE:
              this.setOrderType(value, rowData);
              break;
            case COLUMN_PRIVATE_COMMENT:
              this.setPrivateComment(value, rowData);
              break;
            case COLUMN_START_TIME:
              this.setStartTime(value, rowData);
              break;
            case COLUMN_END_TIME:
              this.setEndTime(value, rowData);
              break;
          }
        }
      }
    }
  }

  /**********************************************************************/
  /* VALUE SETTERS */

  /* Trader Group */
  private setTraderGroup(value: any, row: any): void {
    let rowData: BfxHedgeOrderStagingRow =
      row instanceof BfxHedgeOrderStagingRow
        ? row
        : row != null && row.data instanceof BfxHedgeOrderStagingRow
          ? row.data
          : null;
    if (rowData instanceof BfxHedgeOrderStagingRow && value.value && value.label) {
      let traderGroup: CubiqTS.TradingGroupImpl =
        this.traderGroupLookupService.getTraderGroupByName(value.label);
      let oldTraderGroup: CubiqTS.TradingGroupImpl = rowData.traderGroup;
      rowData.orderRequest.traderGroup = value.label;
      rowData.traderGroup = traderGroup;
      if (
        (oldTraderGroup == null && value.label != null) ||
        oldTraderGroup.groupCode != value.label
      ) {
        this.onTraderGroupChanged(rowData, oldTraderGroup, traderGroup);
      }
    }
  }

  /* Account */
  private setAccount(value: any, row: any): void {
    let rowData: BfxHedgeOrderStagingRow =
      row instanceof BfxHedgeOrderStagingRow
        ? row
        : row != null && row.data instanceof BfxHedgeOrderStagingRow
          ? row.data
          : null;
    if (rowData instanceof BfxHedgeOrderStagingRow) {
      let oldAccountCode: string = rowData.orderRequest.accountCode;
      rowData.orderRequest.accountCode = value.value;
      if (oldAccountCode != value.value) {
        this.onAccountChanged(rowData, oldAccountCode, value.value);
      }
    }
  }

  /* Private Comment */
  private setPrivateComment(value: any, row: any): void {
    let rowData: BfxHedgeOrderStagingRow =
      row instanceof BfxHedgeOrderStagingRow
        ? row
        : row != null && row.data instanceof BfxHedgeOrderStagingRow
          ? row.data
          : null;
    if (rowData instanceof BfxHedgeOrderStagingRow) {
      rowData.orderRequest.comment2 = value;
    }
  }

  /* Start Time */
  private setStartTime(value: any, params: any): void {
    let rowData: BfxHedgeOrderStagingRow =
      params instanceof BfxHedgeOrderStagingRow
        ? params
        : params != null && params.data instanceof BfxHedgeOrderStagingRow
          ? params.data
          : null;
    if (rowData instanceof BfxHedgeOrderStagingRow) {
      if (value !== null) {
        const newTime: CubiqTS.Moment = CubiqTS.moment(this.fixingData.fixingTimestamp);

        newTime.hours(value.hours());
        newTime.minutes(value.minutes());
        newTime.seconds(value.seconds());
        rowData.startTime = newTime;
        if (!(params instanceof BfxHedgeOrderStagingRow)) {
          params.value = newTime;
        }
      } else {
        rowData.startTime = null;
      }
    }
  }

  /* End Time */
  private setEndTime(value: any, params: any): void {
    let rowData: BfxHedgeOrderStagingRow =
      params instanceof BfxHedgeOrderStagingRow
        ? params
        : params != null && params.data instanceof BfxHedgeOrderStagingRow
          ? params.data
          : null;
    if (rowData instanceof BfxHedgeOrderStagingRow) {
      if (value !== null) {
        const newTime: CubiqTS.Moment = CubiqTS.moment(this.fixingData.fixingTimestamp);

        newTime.hours(value.hours());
        newTime.minutes(value.minutes());
        newTime.seconds(value.seconds());
        rowData.endTime = newTime;
        if (!(params instanceof BfxHedgeOrderStagingRow)) {
          params.value = newTime;
        }
      } else {
        rowData.endTime = null;
      }
    }
  }

  /* Order Type */
  private setOrderType(value: any, row: any): void {
    let rowData: BfxHedgeOrderStagingRow =
      row instanceof BfxHedgeOrderStagingRow
        ? row
        : row != null && row.data instanceof BfxHedgeOrderStagingRow
          ? row.data
          : null;
    if (rowData instanceof BfxHedgeOrderStagingRow) {
      let oldAlgoHedge: string =
        rowData != null && rowData.algoHedgeConfig != null
          ? rowData.algoHedgeConfig.algoProfile
          : null;

      if (rowData.traderGroup != null) {
        let traderGroup: CubiqTS.TradingGroupImpl = rowData.traderGroup;
        let orderRequest: CubiqTS.FixingOrderRequestDataImpl = rowData.orderRequest;

        if (!traderGroup.isSupportsBMAlgosFg) {
          orderRequest.orderType = CubiqTS.OrderType.BENCHMARK;
          orderRequest.requestOrderType = CubiqTS.RequestOrderType.BMARK;
          rowData.algoHedgeConfig = null;
        } else {
          orderRequest.orderType = CubiqTS.OrderType.BMALGO;
          orderRequest.requestOrderType = CubiqTS.RequestOrderType.BMALGO;
          rowData.algoHedgeConfig = this.algoHedgeService.getItem(value.value);
          if (rowData.algoHedgeConfig != null) {
            let isChanged: boolean = oldAlgoHedge != rowData.algoHedgeConfig.algoProfile;

            let algoHedgeConfig: CubiqTS.AlgoHedgeConfigInfoImpl = rowData.algoHedgeConfig;

            if (isChanged) {
              orderRequest.strategyParameters = algoHedgeConfig.strategyParams.slice(0);

              this.initializeActiveTimes(algoHedgeConfig, rowData);
            }
          }
        }
      }
    }
  }

  private initializeActiveTimes(
    algoHedgeConfig: CubiqTS.AlgoHedgeConfigInfoImpl,
    rowData: BfxHedgeOrderStagingRow,
  ) {
    let fixingTime: number = this.fixingData.fixingTimestamp;
    if (algoHedgeConfig.startTimeOffset != null) {
      rowData.startTime = CubiqTS.moment(
        fixingTime.valueOf() - algoHedgeConfig.startTimeOffset.toNumber() * 1000,
      );
    }

    if (algoHedgeConfig.endTimeOffset != null) {
      rowData.endTime = CubiqTS.moment(
        fixingTime.valueOf() + algoHedgeConfig.endTimeOffset.toNumber() * 1000,
      );
    }
  }
}
